import Transport from '@/lib/api/transport'
import ApiBase from '@/lib/api/v1/api-base'

import {
  ApiAddProductToCartPayload,
  ApiAddProductToCartResponse,
  ApiCart,
  ApiEditProductPayload,
  ApiPlaceQuotePayload,
  ApiQuote,
  ApiUnitCustomer,
} from '@/types'

class Cart extends ApiBase {
  constructor(transport: Transport) {
    super(transport)
    this.getCart = this.getCart.bind(this)
    this.setCartShippingAddressId = this.setCartShippingAddressId.bind(this)
    this.setCartBillingAddressId = this.setCartBillingAddressId.bind(this)
    this.setPaymentMethod = this.setPaymentMethod.bind(this)
    this.updateShippingNotificationEmails = this.updateShippingNotificationEmails.bind(this)
    this.updateBillingNotificationEmails = this.updateBillingNotificationEmails.bind(this)
    this.addProducts = this.addProducts.bind(this)
    this.updateProduct = this.updateProduct.bind(this)
    this.deleteProducts = this.deleteProducts.bind(this)
    this.duplicateProduct = this.duplicateProduct.bind(this)
    this.getDeliveryContacts = this.getDeliveryContacts.bind(this)
    this.setDeliveryContact = this.setDeliveryContact.bind(this)
    this.setProductShippingAddress = this.setProductShippingAddress.bind(this)
    this.deleteProductShippingAddress = this.deleteProductShippingAddress.bind(this)
    this.setProductDeliveryContact = this.setProductDeliveryContact.bind(this)
    this.placeQuote = this.placeQuote.bind(this)
    this.deleteCart = this.deleteCart.bind(this)
    this.setPromo = this.setPromo.bind(this)
  }

  /**
   * Return cart.
   */
  getCart({ recalculate }: { recalculate: boolean }, userId?: string) {
    return this._withDecoratedPromise<ApiCart>(() => {
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(
          userId,
        )}/carts/current/v2?recalculate=${encodeURIComponent(recalculate)}`,
      )
    })
  }

  setCartShippingAddressId(payload: { addressId: string }, userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/addresses/delivery`,
        payload,
      )
    })
  }

  setCartBillingAddressId(payload: { addressId: string }, userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/addresses/billing`,
        payload,
      )
    })
  }

  setPaymentMethod({ data }: { data: object }, userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/paymentmethod`,
        data,
      )
    })
  }

  updateShippingNotificationEmails(emails: string[], userId?: string) {
    return this._withDecoratedPromise(() => {
      const payload = { emails: emails }
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/delivery/notifications`,
        payload,
      )
    })
  }

  updateBillingNotificationEmails(emails: string[], userId?: string) {
    return this._withDecoratedPromise(() => {
      const payload = { emails: emails }
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/billing/notifications`,
        payload,
      )
    })
  }

  addProducts(data: ApiAddProductToCartPayload[], userId?: string) {
    return this._withDecoratedPromise<ApiAddProductToCartResponse>(() => {
      return this.transport.post(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/v2/entries`,
        data,
      )
    })
  }

  updateProduct(entryId: string, data: object, userId?: string) {
    return this._withDecoratedPromise<ApiEditProductPayload>(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/v2/entries/${entryId}`,
        data,
      )
    })
  }

  deleteProducts(entryNumbers: Record<number, object>, userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.delete(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/entries`,
        entryNumbers,
      )
    })
  }

  duplicateProduct(entryId: number, userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.post(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/entries/${entryId}/duplicate`,
      )
    })
  }

  getDeliveryContacts(userId?: string) {
    return this._withDecoratedPromise<ApiUnitCustomer[]>(() => {
      return this.transport.get(`/rest/api/v1/users/${this._getUserId(userId)}/unitcustomers`)
    })
  }

  /**
   * Apply delivery contact to cart.
   */
  setDeliveryContact(data: { customerUid: string }, userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/delivery/contact`,
        data,
      )
    })
  }

  /**
   * Apply delivery address for cart entry.
   */
  setProductShippingAddress(
    entryId: number,
    data: {
      addressId: string
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(
          userId,
        )}/carts/current/entries/${entryId}/addresses/delivery`,
        data,
      )
    })
  }

  /**
   * Remove delivery address from cart entry.
   */
  deleteProductShippingAddress(
    entryId: string,
    data: {
      addressId: string
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise(() => {
      return this.transport.delete(
        `/rest/api/v1/users/${this._getUserId(
          userId,
        )}/carts/current/entries/${entryId}/addresses/delivery`,
        data,
      )
    })
  }

  /**
   * Apply delivery contact for cart entry.
   */
  setProductDeliveryContact(
    entryId: string,
    data: {
      customerUid: string | null
    },
    userId?: string,
  ) {
    return this._withDecoratedPromise(() => {
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(
          userId,
        )}/carts/current/entries/${entryId}/contact/delivery`,
        data,
      )
    })
  }
  placeQuote(data: ApiPlaceQuotePayload, userId?: string) {
    return this._withDecoratedPromise<{ quotes: ApiQuote[] }>(() => {
      return this.transport.post(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/quote`,
        data,
      )
    })
  }

  placePunchoutQuote(userId?: string) {
    return this._withDecoratedPromise<{ browseFormPostUrl: string; orderAsCXML: string }>(() => {
      return this.transport.get(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/punchout/cxml/requisition`,
      )
    })
  }

  deleteCart(userId?: string) {
    return this._withDecoratedPromise(() => {
      return this.transport.delete(`/rest/api/v1/users/${this._getUserId(userId)}/carts/current`)
    })
  }

  setPromo({ promoCode }: { promoCode: string }, userId?: string) {
    return this._withDecoratedPromise(() => {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
      return this.transport.put(
        `/rest/api/v1/users/${this._getUserId(userId)}/carts/current/setPromo`,
        promoCode,
        headers,
      )
    })
  }
}

export default Cart
